/**
 * Monitoring health and filters for the beer-ingestion workspace (ITT-892).
 */

export const BEER_INGESTION_MONITORING_FILTER_ALL = 'all';

export const BEER_INGESTION_MONITORING_FILTER_LABELS = {
  monitored: 'Monitored',
  not_monitored: 'Not monitored',
  paused: 'Paused',
  failing: 'Failing',
  up_to_date: 'Unchanged (up to date)',
  extract_issue: 'Extract pending or failed',
};

/** In-flight extract statuses older than this are treated as stuck (30 minutes). */
export const EXTRACTION_STUCK_THRESHOLD_MS = 30 * 60 * 1000;

export const BEER_INGESTION_MONITORING_FILTERS = Object.keys(
  BEER_INGESTION_MONITORING_FILTER_LABELS,
);

export function formatBeerIngestionMonitoringFilterLabel(filterValue) {
  return BEER_INGESTION_MONITORING_FILTER_LABELS[filterValue] || filterValue;
}

export function getTrustedMonitoringSource(row) {
  return row?.trustedSource || null;
}

function isInFlightExtractStatus(status) {
  return status === 'queued' || status === 'running';
}

export function isStuckExtraction(source, now = new Date()) {
  if (!source || !isInFlightExtractStatus(source.lastExtractStatus)) {
    return false;
  }
  const referenceAt = source.lastCheckedAt || source.lastChangedAt;
  if (!referenceAt) {
    return false;
  }
  const referenceDate =
    referenceAt instanceof Date ? referenceAt : new Date(referenceAt);
  if (Number.isNaN(referenceDate.getTime())) {
    return false;
  }
  return now.getTime() - referenceDate.getTime() > EXTRACTION_STUCK_THRESHOLD_MS;
}

export function hasExtractIssue(source) {
  if (!source) {
    return false;
  }
  if (source.lastExtractStatus === 'failure') {
    return true;
  }
  if (isInFlightExtractStatus(source.lastExtractStatus)) {
    return true;
  }
  return isStuckExtraction(source);
}

function getSameCheckExtractIssueHealth(source) {
  if (source.lastExtractStatus === 'failure') {
    return {
      key: 'extract_issue',
      label: 'Extract failed',
      color: 'warning',
    };
  }
  if (isStuckExtraction(source)) {
    return {
      key: 'extract_issue',
      label: 'Extract stuck',
      color: 'error',
    };
  }
  return {
    key: 'extract_issue',
    label: 'Extract pending',
    color: 'warning',
  };
}

export function getBeerIngestionMonitoringHealth(row) {
  const source = getTrustedMonitoringSource(row);
  if (!source || row?.derivedStatus !== 'trusted') {
    return {
      key: 'none',
      label: '—',
      color: 'default',
    };
  }
  if (source.monitoringStatus === 'paused') {
    const pausedLabel = source.pausedReason === 'repeated_failures'
      ? 'Paused (auto)'
      : 'Paused';
    const pausedTooltip = source.pausedReason === 'repeated_failures'
      ? 'Monitoring was automatically paused after repeated failures. Resume after fixing the issue.'
      : undefined;
    return {
      key: 'paused',
      label: pausedLabel,
      color: source.pausedReason === 'repeated_failures' ? 'error' : 'default',
      tooltip: pausedTooltip,
    };
  }
  if (!source.monitorId) {
    return {
      key: 'not_monitored',
      label: 'Not monitored',
      color: 'warning',
    };
  }
  if (!source.lastCheckedAt) {
    return {
      key: 'pending_first_check',
      label: 'Pending first check',
      color: 'info',
    };
  }
  if (source.lastCheckStatus === 'error' || source.lastCheckStatus === 'removed') {
    return {
      key: 'failing',
      label: 'Failing',
      color: 'error',
    };
  }
  if (source.lastCheckStatus === 'same' && hasExtractIssue(source)) {
    return getSameCheckExtractIssueHealth(source);
  }
  if (source.lastCheckStatus === 'same') {
    return {
      key: 'up_to_date',
      label: 'Up to date',
      color: 'success',
    };
  }
  if (source.lastCheckStatus === 'changed' || source.lastCheckStatus === 'new') {
    if (source.lastExtractStatus === 'failure') {
      return {
        key: 'extract_issue',
        label: 'Changed · extract failed',
        color: 'warning',
      };
    }
    if (isStuckExtraction(source)) {
      return {
        key: 'extract_issue',
        label: 'Changed · extract stuck',
        color: 'error',
      };
    }
    return {
      key: 'changed',
      label:
        source.lastExtractStatus === 'queued' || source.lastExtractStatus === 'running'
          ? 'Changed · extract pending'
          : 'Changed',
      color: 'warning',
    };
  }
  return {
    key: 'monitored',
    label: 'Monitored',
    color: 'info',
  };
}

export function formatRelativeCheckTime(value, now = new Date()) {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const deltaMs = now.getTime() - date.getTime();
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  if (deltaMs < minuteMs) {
    return 'just now';
  }
  if (deltaMs < hourMs) {
    const minutes = Math.floor(deltaMs / minuteMs);
    return `${minutes}m ago`;
  }
  if (deltaMs < dayMs) {
    const hours = Math.floor(deltaMs / hourMs);
    return `${hours}h ago`;
  }
  const days = Math.floor(deltaMs / dayMs);
  return `${days}d ago`;
}

export function formatBeerIngestionMonitoringTooltip(source) {
  if (!source) {
    return 'Not a trusted official source';
  }
  const cadence = source.monitorCadence || 'weekly';
  const lastCheck = formatLastCheckForTooltip(source);
  const lastExtract = formatLastExtractForTooltip(source);
  const failures = source.failureCount || 0;
  const pauseNote = source.pausedReason === 'repeated_failures'
    ? ' Auto-paused after repeated failures.'
    : '';
  return `Checked ${cadence}. Last check: ${lastCheck}. ${lastExtract} Failures: ${failures}.${pauseNote}`;
}

function formatLastCheckForTooltip(source) {
  const status = source.lastCheckStatus;
  if (!status) {
    return 'never';
  }
  if (status === 'same') {
    return 'unchanged';
  }
  if (status === 'new') {
    return 'first check';
  }
  if (status === 'changed') {
    if (source.lastCheckMeaningful === true) {
      return 'changed (beer list)';
    }
    if (source.lastCheckMeaningful === false) {
      return 'changed (not a beer-list change)';
    }
    return 'changed';
  }
  if (status === 'removed') {
    return 'page removed';
  }
  if (status === 'error') {
    return 'error';
  }
  return status;
}

function formatLastExtractForTooltip(source) {
  const extractStatus = source.lastExtractStatus;
  if (!extractStatus) {
    return 'Last extract: none.';
  }
  if (extractStatus === 'queued' || extractStatus === 'running') {
    return 'Last extract: pending.';
  }
  if (extractStatus === 'failure') {
    return 'Last extract: failed.';
  }
  const beerCount = source.lastExtractBeerCount;
  if (typeof beerCount === 'number' && Number.isFinite(beerCount) && beerCount >= 0) {
    const beerLabel = beerCount === 1 ? 'beer' : 'beers';
    return `Last extract: ${beerCount} ${beerLabel}.`;
  }
  return 'Last extract: success.';
}

export function matchesBeerIngestionMonitoringFilter(row, monitoringFilter) {
  if (!monitoringFilter || monitoringFilter === BEER_INGESTION_MONITORING_FILTER_ALL) {
    return true;
  }
  const source = getTrustedMonitoringSource(row);
  if (monitoringFilter === 'paused') {
    return source?.monitoringStatus === 'paused';
  }
  if (monitoringFilter === 'not_monitored') {
    return Boolean(source) && row.derivedStatus === 'trusted' && !source.monitorId;
  }
  if (monitoringFilter === 'monitored') {
    return Boolean(source?.monitorId) && source.monitoringStatus === 'active';
  }
  if (monitoringFilter === 'failing') {
    return source?.lastCheckStatus === 'error' || source?.lastCheckStatus === 'removed';
  }
  if (monitoringFilter === 'up_to_date') {
    return source?.lastCheckStatus === 'same';
  }
  if (monitoringFilter === 'extract_issue') {
    return hasExtractIssue(source);
  }
  return true;
}

export function summarizeBeerIngestionMonitoring(rows = []) {
  const trustedRows = rows.filter((row) => row.derivedStatus === 'trusted' && row.trustedSource);
  const monitored = trustedRows.filter((row) => row.trustedSource?.monitorId).length;
  const failing = trustedRows.filter((row) => {
    const status = row.trustedSource?.lastCheckStatus;
    return status === 'error' || status === 'removed';
  }).length;
  const unmonitoredTrusted = trustedRows.filter((row) => !row.trustedSource?.monitorId).length;
  const checkedAtValues = trustedRows
    .map((row) => row.trustedSource?.lastCheckedAt)
    .filter(Boolean)
    .map((value) => (value instanceof Date ? value : new Date(value)))
    .filter((date) => !Number.isNaN(date.getTime()));
  const lastCheckRecorded =
    checkedAtValues.length > 0
      ? new Date(Math.max(...checkedAtValues.map((date) => date.getTime())))
      : null;
  return {
    trusted: trustedRows.length,
    monitored,
    failing,
    unmonitoredTrusted,
    lastCheckRecorded,
  };
}

export function formatBeerIngestionMonitoringSummary(summary) {
  const lastCheck = summary.lastCheckRecorded
    ? formatRelativeCheckTime(summary.lastCheckRecorded)
    : 'never';
  return `Monitored: ${summary.monitored} / ${summary.trusted} trusted · Last check recorded: ${lastCheck} · Failing: ${summary.failing}`;
}
